import { ADOPTION_STATUSES } from './data.js';

/**
 * Definitions PARTAGEES entre les ecrans de Cat Manager Six et son ecran de
 * reglages (settings.tsx, monte par le hub Parametres du shell) : catalogues
 * de filtres et de stats par section (Chats, Reservations), cles de prefs et
 * valeurs par defaut. Un seul endroit, zero duplication.
 *
 * Choix des cles (l'app a DEUX ecrans a reglages, fusionnes dans un settings) :
 *  - "app.cat-manager-six.filters" : UNE cle Record<filterId, boolean> qui
 *    fusionne les deux jeux de pastilles, ids prefixes par section
 *    ("cats.<statut>" pour l'ecran Les chats, "visits.<periode>" pour l'ecran
 *    Les visites). Cle absente = visible, seul un `false` explicite masque.
 *  - stats : une cle string[] (ids ordonnes, max 3) PAR section,
 *    "app.cat-manager-six.stats.cats" et "app.cat-manager-six.stats.visits" :
 *    chaque ecran affiche ses propres stats, un plafond de 3 partage entre les
 *    deux sections n'aurait pas de sens.
 * Les prefs sont locales au poste (localStorage via usePref de @taki/ui).
 */

/* ---- Filtres (zone 4), cle fusionnee ------------------------------------ */

/** Cle de pref : visibilite des pastilles de filtre des DEUX ecrans. */
export const FILTERS_PREF_KEY = 'app.cat-manager-six.filters';

/** Id de pref d'une pastille de statut de l'ecran Les chats. */
export function catFilterPrefId(status: string): string {
  return `cats.${status}`;
}

/** Id de pref d'une pastille de periode de l'ecran Les visites. */
export function visitFilterPrefId(range: string): string {
  return `visits.${range}`;
}

/** Periode affichee par la bascule de la zone 4 de l'ecran Les visites. */
export type RangeId = 'today' | 'week';

/** Pastilles de periode de l'ecran Les visites (bascule Aujourd'hui / Cette semaine). */
export const RANGE_OPTIONS: ReadonlyArray<{ id: RangeId; label: string }> = [
  { id: 'today', label: "Aujourd'hui" },
  { id: 'week', label: 'Cette semaine' },
];

/**
 * Defaut filtres : toutes les pastilles visibles. La pastille 'Tous' de
 * l'ecran Les chats n'est pas dans le catalogue : c'est le repli, elle reste
 * toujours affichee.
 */
export const DEFAULT_FILTERS: Record<string, boolean> = Object.fromEntries([
  ...ADOPTION_STATUSES.map((status) => [catFilterPrefId(status.value), true]),
  ...RANGE_OPTIONS.map((option) => [visitFilterPrefId(option.id), true]),
]);

/* ---- Stats d'en-tete (zone 3), une cle par section ----------------------- */

/** Nombre maximal de stats affichees par ecran (contrat des cles de stats). */
export const STATS_MAX = 3;

/** Ids des statistiques disponibles sur l'ecran Les chats. */
export type CatStatId = 'total' | 'adoptable' | 'adopte' | 'vaccines';

/**
 * Catalogue des stats de l'ecran Les chats. La SELECTION (max 3) est editable
 * dans settings.tsx ; l'ordre d'affichage suit ce catalogue et les valeurs
 * restent calculees des donnees reelles par l'ecran.
 */
export const CAT_STAT_DEFS: ReadonlyArray<{ id: CatStatId; label: string }> = [
  { id: 'total', label: 'chats' },
  { id: 'adoptable', label: 'adoptables' },
  { id: 'adopte', label: 'adoptes' },
  { id: 'vaccines', label: 'vaccins a jour' },
];

/** Cle de pref : ids ordonnes des stats affichees sur l'ecran Les chats. */
export const CATS_STATS_PREF_KEY = 'app.cat-manager-six.stats.cats';

/** Defaut stats chats : les 3 premieres du catalogue. */
export const DEFAULT_CAT_STATS: string[] = CAT_STAT_DEFS.slice(0, STATS_MAX).map(
  (def) => def.id,
);

/** Ids des statistiques disponibles sur l'ecran Les visites. */
export type VisitStatId = 'period' | 'arrived' | 'cancelled' | 'tables-free';

/**
 * Catalogue des stats de l'ecran Les visites. Memes regles que le catalogue
 * des chats ; 'period', 'arrived' et 'cancelled' comptent sur la periode
 * affichee, 'tables-free' porte sur aujourd'hui.
 */
export const VISIT_STAT_DEFS: ReadonlyArray<{ id: VisitStatId; label: string }> = [
  { id: 'period', label: 'visites' },
  { id: 'arrived', label: 'arrivees' },
  { id: 'cancelled', label: 'annulees' },
  { id: 'tables-free', label: 'tables libres' },
];

/** Cle de pref : ids ordonnes des stats affichees sur l'ecran Les visites. */
export const VISITS_STATS_PREF_KEY = 'app.cat-manager-six.stats.visits';

/** Defaut stats visites : les 3 premieres du catalogue. */
export const DEFAULT_VISIT_STATS: string[] = VISIT_STAT_DEFS.slice(0, STATS_MAX).map(
  (def) => def.id,
);
